/**
 * identityMemory.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Browser memory that belongs to WHOEVER IS SIGNED IN, not to the browser.
 *
 * REPORTED 4 Oct 2026: a super admin signed in as Yalelo ("Sign in as vendor")
 * and Yalelo's Catalog opened on China Town's products, titled "Product
 * Management - China Town". The page remembered its last brand in session
 * storage under one fixed key so that a refresh would not lose it. Earlier in
 * the same tab the admin had been working on China Town, and "Sign in as
 * vendor" swaps the session in place, in the same tab, so Yalelo's page found
 * the admin's China Town and used it ahead of looking up the vendor's own
 * brand. The server refused every write (the brand catalogue routes check
 * ownership), so nothing was damaged, but the page was showing one vendor the
 * catalogue of another.
 *
 * The same thing happens without impersonation wherever two people share a
 * browser: the call centre machines, or a vendor signing in after an admin.
 *
 * The rule: a remembered choice that names a specific vendor, brand, outlet
 * or record is keyed by the signed-in identity. When the identity changes, the
 * memory simply is not there, and the page falls back to what it would do on
 * a first visit. When the admin returns, their own memory is waiting for them.
 *
 * `storage` is "session" (this tab only; the default) or "local" (this
 * browser). Every access can throw (private windows, blocked site data), and a
 * memory that cannot be read or written is not worth an error, so the
 * fallback is always the caller's.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { getUserFromLocalStorage } from "./user";

/** The signed-in user's id, or "anon" when nobody is (a key still has to exist). */
export const signedInIdentity = () => {
  try {
    return String(getUserFromLocalStorage()?.result?._id || "anon");
  } catch (err) {
    return "anon";
  }
};

/** `key` as stored for the signed-in identity. */
export const identityKey = (key) => `${key}.${signedInIdentity()}`;

const storageArea = (storage) => {
  try {
    return storage === "local" ? window.localStorage : window.sessionStorage;
  } catch (err) {
    return null;
  }
};

/** What the signed-in identity last remembered under `key`, or `fallback`. */
export const recall = (key, { storage = "session", fallback = null } = {}) => {
  try {
    const raw = storageArea(storage)?.getItem(identityKey(key));
    return raw == null ? fallback : JSON.parse(raw);
  } catch (err) {
    return fallback;
  }
};

/** Remember `value` for the signed-in identity. null or undefined forgets it. */
export const remember = (key, value, { storage = "session" } = {}) => {
  try {
    const area = storageArea(storage);
    if (!area) return;
    if (value == null) area.removeItem(identityKey(key));
    else area.setItem(identityKey(key), JSON.stringify(value));
  } catch (err) {
    /* A memory that cannot be saved is not worth an error. */
  }
};
